import {useEffect, useMemo, useRef, useState} from "react";
import {Empty, Segmented, Spin} from "antd";
import className from "classnames/bind";
import {FontAwesomeIcon} from "~/components";
import {useGetGscContentQuery, useEnrichGscContentMutation} from "~/reduxs/api/gscApiSlice";
import {TrendBadge, bucketRows, BUCKETS} from "./GscTrend";
import style from "../style/Gsc.module.scss";

const cn = className.bind(style);

const fmt = (n) => Number(n || 0).toLocaleString('vi-VN');

/** Rút gọn URL để hiển thị (bỏ scheme, cắt bớt nếu dài). */
function shortUrl(url) {
	const s = String(url || '').replace(/^https?:\/\//, '');
	return s.length > 60 ? s.slice(0, 60) + '…' : s;
}

/**
 * Khối "Nội dung của bạn": top trang kèm ảnh thumbnail + tiêu đề (tự crawl og:image/title,
 * điền dần qua enrich). 3 nhóm Đầu trang / Đang tăng / Đang giảm giống GSC.
 */
function GscContent({projectId, from, to}) {

	const [bucket, setBucket] = useState('top');

	const {data, isFetching, refetch} = useGetGscContentQuery(
		{projectId, from, to, limit: 24},
		{skip: !from || !to},
	);

	const [enrich, {isLoading: enriching}] = useEnrichGscContentMutation();
	const rounds = useRef(0);

	// Đổi khoảng ngày → reset bộ đếm vòng poll.
	useEffect(() => { rounds.current = 0; }, [from, to, projectId]);

	// Còn trang thiếu meta → gọi enrich (bounded) rồi refetch, lặp tới khi hết (cap 12 vòng).
	useEffect(() => {
		if (!data || enriching) return;
		if (data.pending > 0 && rounds.current < 12) {
			rounds.current += 1;
			(async () => {
				try {
					await enrich({projectId, from, to}).unwrap();
					await refetch();
				} catch (e) { /* nuốt lỗi: vòng sau thử tiếp hoặc dừng */ }
			})();
		}
	}, [data, enriching, projectId, from, to, enrich, refetch]);

	const rows = useMemo(() => bucketRows(data?.rows || [], bucket), [data, bucket]);

	if (isFetching && !data) {
		return <div style={{textAlign: 'center', padding: '30px 0'}}><Spin /></div>;
	}

	if (!data?.rows?.length) {
		return <Empty description="Chưa có dữ liệu trang trong khoảng này." image={Empty.PRESENTED_IMAGE_SIMPLE} />;
	}

	return (
		<>
			<div className={cn('rangeBar')} style={{marginBottom: 12}}>
				<Segmented options={BUCKETS} value={bucket} onChange={setBucket} size="small" />
				{(data.pending > 0 || enriching) && (
					<span style={{color: '#9ca3af', fontSize: 12}}>
						<Spin size="small" /> Đang lấy tiêu đề/ảnh ({data.pending} còn lại)…
					</span>
				)}
			</div>

			<div className={cn('contentList')}>
				{rows.map((r) => (
					<a key={r.value} href={r.value} target="_blank" rel="noreferrer" className={cn('contentRow')}>
						<div className={cn('thumb')}>
							{r.image
								? <img src={r.image} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
								: <FontAwesomeIcon icon="fa-light fa-image" />}
						</div>
						<div className={cn('contentMain')}>
							<div className={cn('contentTitle')}>{r.title || shortUrl(r.value)}</div>
							<div className={cn('contentUrl')}>{shortUrl(r.value)}</div>
						</div>
						<div className={cn('contentStats')}>
							<TrendBadge value={r.delta_pct} />
							<span className={cn('contentClicks')}>{fmt(r.clicks)}</span>
						</div>
					</a>
				))}
			</div>
		</>
	);
}

export default GscContent;
